import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { RouterModule, ActivatedRoute } from '@angular/router';
import { PortalNavbarComponent } from './portal-navbar.component';
import { ManagedUserDetailDto, ManagedUserMediaDto, UserManagementService } from '../services/user-management.service';

@Component({
  selector: 'app-user-detail-page',
  standalone: true,
  imports: [CommonModule, RouterModule, PortalNavbarComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>

      <section class="page-hero">
        <div class="page-hero-inner">
          <a routerLink="/users" class="home-btn">Users</a>
          <h1 class="page-title">{{ userName() }}</h1>
          <p class="page-sub">User details, organization memberships, and uploaded media playback.</p>
        </div>
      </section>

      <section class="page-body">
        <div class="page-body-inner">
          @if (message()) {
            <div class="status-banner">{{ message() }}</div>
          }

          @if (detail()) {
            <div class="card">
              <div class="card-hd"><span class="card-title">Profile</span></div>
              <div class="profile-grid">
                <div><strong>Email:</strong> {{ detail()!.email || '—' }}</div>
                <div><strong>Entra Id:</strong> {{ detail()!.entraUserId || '—' }}</div>
              </div>
            </div>

            <div class="card">
              <div class="card-hd"><span class="card-title">Organization Memberships</span></div>
              <div class="table-wrap">
                <div class="table-head"><span>Organization</span><span>Role</span><span>SMS</span><span>Media Upload</span></div>
                @for (m of detail()!.memberships; track m.organizationId + '-' + m.role) {
                  <div class="table-row"><span>{{ m.organizationName || ('Org ' + m.organizationId) }}</span><span>{{ m.role }}</span><span>{{ m.smsNotificationsEnabled ? 'Opted In' : 'Not Opted In' }}</span><span>{{ m.mediaUploadConsentEnabled ? 'Opted In' : 'Not Opted In' }}</span></div>
                } @empty {
                  <div class="empty">No memberships found.</div>
                }
              </div>
            </div>

            <div class="card">
              <div class="card-hd"><span class="card-title">Uploaded Media</span></div>
              <div class="table-wrap">
                <div class="table-head"><span>Type</span><span>File</span><span>Category</span><span>Actions</span></div>
                @for (media of detail()!.media; track media.artifactId) {
                  <div class="table-row">
                    <span>{{ media.artifactType }}</span>
                    <span>{{ media.fileName }}</span>
                    <span>{{ media.artifactCategory || '—' }}</span>
                    <button class="btn btn--ghost" (click)="togglePreview(media)">{{ activeMediaId() === media.artifactId ? 'Hide' : 'Play' }}</button>
                  </div>
                  @if (activeMediaId() === media.artifactId && (previewUrl() || previewErrorMessage())) {
                    <div class="preview-row">
                      @if (previewUrl()) {
                        @if (media.artifactType.toLowerCase() === 'sound') {
                          <audio controls [src]="previewUrl()" (loadeddata)="clearMediaDecodeError()" (error)="onMediaDecodeError($event, media)"></audio>
                        } @else {
                          <video class="video-preview" controls [src]="previewUrl()" (loadeddata)="clearMediaDecodeError()" (error)="onMediaDecodeError($event, media)"></video>
                        }
                      }
                      @if (previewErrorMessage()) {
                        <div class="preview-error">{{ previewErrorMessage() }}</div>
                      }
                    </div>
                  }
                } @empty {
                  <div class="empty">No user media available.</div>
                }
              </div>
            </div>
          }
        </div>
      </section>
    </div>
  `,
  styles: [`
    .page{min-height:100vh}.page-hero{border-bottom:1px solid var(--color-border);padding:2rem 1.5rem;background:linear-gradient(155deg,var(--color-bg-start) 0%,var(--color-bg-end) 100%)}
    .page-hero-inner,.page-body-inner{max-width:1100px;margin:0 auto}.home-btn{color:var(--color-muted);text-decoration:none}.page-title{margin:.6rem 0 0}.page-sub{margin:.4rem 0 0;color:var(--color-muted)}
    .page-body{padding:1.5rem}.status-banner{margin-bottom:.8rem;border:1px solid var(--color-border);border-radius:.6rem;padding:.55rem .75rem}
    .card{border:1px solid var(--color-border);border-radius:.9rem;overflow:hidden;background:color-mix(in srgb,var(--color-panel) 80%,transparent);margin-bottom:1rem}
    .card-hd{border-bottom:1px solid var(--color-border);padding:.8rem 1rem}.card-title{font-weight:600}.profile-grid{padding:1rem;display:grid;gap:.55rem}
    .table-wrap{display:grid}.table-head,.table-row{display:grid;grid-template-columns:1fr 1fr 1fr auto;gap:.75rem;padding:.7rem 1rem;align-items:center}
    .table-head{font-size:.7rem;text-transform:uppercase;letter-spacing:.06em;color:var(--color-muted);border-bottom:1px solid var(--color-border)}
    .table-row{border-bottom:1px solid color-mix(in srgb,var(--color-border) 50%,transparent)}.table-row:last-child{border-bottom:none}
    .btn{border:1px solid var(--color-border);border-radius:.45rem;background:transparent;color:var(--color-muted);padding:.35rem .5rem;cursor:pointer}
    .btn:hover{color:var(--color-text)}.preview-row{padding:.7rem 1rem;border-bottom:1px solid color-mix(in srgb,var(--color-border) 40%,transparent)}
    .video-preview{max-width:min(100%,460px);border:1px solid var(--color-border);border-radius:.55rem}.empty{padding:1rem;color:var(--color-muted);text-align:center}
    .preview-error{margin-top:.55rem;border:1px solid color-mix(in srgb,#ef4444 45%,transparent);background:color-mix(in srgb,#ef4444 10%,transparent);color:#fecaca;border-radius:.5rem;padding:.45rem .6rem;font-size:.82rem}
  `]
})
export class UserDetailPageComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly service = inject(UserManagementService);

  readonly detail = signal<ManagedUserDetailDto | null>(null);
  readonly message = signal('');
  readonly activeMediaId = signal<number | null>(null);
  readonly previewUrl = signal<string | null>(null);
  readonly previewErrorMessage = signal('');

  readonly userName = computed(() => {
    const d = this.detail();
    if (!d) return 'User Detail';
    const full = `${d.firstName ?? ''} ${d.lastName ?? ''}`.trim();
    return full || d.email || `User ${d.userId}`;
  });

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!id || Number.isNaN(id)) {
      this.message.set('Invalid user id.');
      return;
    }

    this.service.getManagedUserDetail(id).subscribe({
      next: detail => this.detail.set(detail),
      error: () => this.message.set('Unable to load user details.')
    });
  }

  togglePreview(media: ManagedUserMediaDto): void {
    if (this.activeMediaId() === media.artifactId) {
      this.clearPreview();
      return;
    }

    this.previewErrorMessage.set('');

    this.service.getUserMediaContent(media.artifactId).subscribe({
      next: blob => {
        this.clearPreview();
        this.activeMediaId.set(media.artifactId);
        const mediaType = this.resolveMediaType(blob, media);
        if (media.artifactType.toLowerCase() === 'video' && !this.canBrowserPlayVideoType(mediaType)) {
          console.warn('[UserDetail] Browser cannot decode video type', { fileName: media.fileName, mediaType });
          this.previewErrorMessage.set(`This browser cannot decode '${media.fileName}' (${mediaType || 'unknown codec'}). Try MP4 (H.264/AAC).`);
          return;
        }

        this.previewUrl.set(this.createPlayableBlobUrl(blob, mediaType));
      },
      error: () => this.message.set('Unable to load media preview.')
    });
  }

  ngOnDestroy(): void {
    this.clearPreview();
  }

  private clearPreview(): void {
    if (this.previewUrl()) {
      URL.revokeObjectURL(this.previewUrl()!);
    }
    this.previewUrl.set(null);
    this.activeMediaId.set(null);
    this.previewErrorMessage.set('');
  }

  clearMediaDecodeError(): void {
    if (this.previewErrorMessage()) {
      this.previewErrorMessage.set('');
    }
  }

  onMediaDecodeError(event: Event, media: ManagedUserMediaDto): void {
    const element = event.target as HTMLMediaElement | null;
    const code = element?.error?.code;

    let detail = 'Unsupported format or codec.';
    if (code === MediaError.MEDIA_ERR_DECODE) {
      detail = 'Decode failed. The browser cannot decode this file.';
    } else if (code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
      detail = 'Source format is not supported by this browser.';
    }

    this.previewErrorMessage.set(`Unable to play '${media.fileName}'. ${detail}`);
    console.warn('[UserDetail] Media decode error', { fileName: media.fileName, code, detail });
  }

  private createPlayableBlobUrl(blob: Blob, mediaType: string): string {
    if (!mediaType) {
      return URL.createObjectURL(blob);
    }

    const typedBlob = new Blob([blob], { type: mediaType });
    return URL.createObjectURL(typedBlob);
  }

  private resolveMediaType(blob: Blob, media: ManagedUserMediaDto): string {
    const mediaType = (media.artifactType ?? '').toLowerCase();
    const fallbackType = mediaType === 'sound' ? 'audio/mpeg' : this.resolveVideoContentType(media.fileName);
    const normalizedBlobType = (blob.type ?? '').trim().toLowerCase();
    const normalizedMediaType = (media.contentType ?? '').trim().toLowerCase();

    if (normalizedBlobType && normalizedBlobType !== 'application/octet-stream') {
      return normalizedBlobType;
    }

    return normalizedMediaType || fallbackType;
  }

  private canBrowserPlayVideoType(mediaType: string): boolean {
    if (!mediaType) {
      return false;
    }

    const video = document.createElement('video');
    return video.canPlayType(mediaType) !== '';
  }

  private resolveVideoContentType(fileName: string): string {
    const lower = (fileName ?? '').toLowerCase();
    if (lower.endsWith('.wmv')) {
      return 'video/x-ms-wmv';
    }
    if (lower.endsWith('.mov')) {
      return 'video/quicktime';
    }
    return 'video/mp4';
  }
}
